import { html, type TemplateResult } from '@design.estate/dees-element';

/**
 * A lightweight, dependency-free markdown renderer shared by the foss.global view
 * components (releases, issues, repo readme, ...). It is intentionally small — headings,
 * unordered lists, fenced code blocks, inline code and links — matching the Claude Design
 * prototype's renderer. Pure functions (no element/`this` state) so any component can import
 * them. Style the output with the `.md` class from the host component.
 */
export type TInlinePart = string | TemplateResult;

/** Render inline markdown: `code` spans and [label](https url) links. */
export function renderInlineMarkdown(textArg: string): TInlinePart[] {
  const parts: TInlinePart[] = [];
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|`([^`]+)`/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(textArg))) {
    if (match.index > lastIndex) {
      parts.push(textArg.slice(lastIndex, match.index));
    }
    if (match[1] && match[2]) {
      parts.push(html`<a href=${match[2]} target="_blank" rel="noopener">${match[1]}</a>`);
    } else if (match[3]) {
      parts.push(html`<code>${match[3]}</code>`);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < textArg.length) {
    parts.push(textArg.slice(lastIndex));
  }
  return parts;
}

/** Render block-level markdown (h1-h3, paragraphs, `-`/`*` lists, fenced code), capped at `maxLinesArg`. */
export function renderMarkdown(markdownArg: string, maxLinesArg = 120): TemplateResult[] {
  const lines = markdownArg.split('\n').slice(0, maxLinesArg);
  const blocks: TemplateResult[] = [];
  let codeBuffer: string[] = [];
  let listBuffer: string[] = [];
  let inCode = false;

  const flushCode = () => {
    if (codeBuffer.length) {
      blocks.push(html`<pre>${codeBuffer.join('\n')}</pre>`);
      codeBuffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push(html`<ul>${listBuffer.map((item) => html`<li>${renderInlineMarkdown(item)}</li>`)}</ul>`);
      listBuffer = [];
    }
  };

  for (const line of lines) {
    if (line.trim().startsWith('```')) {
      if (inCode) {
        flushCode();
      } else {
        flushList();
      }
      inCode = !inCode;
      continue;
    }

    if (inCode) {
      codeBuffer.push(line);
      continue;
    }

    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      continue;
    }

    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(trimmed.slice(2));
      continue;
    }

    flushList();
    if (trimmed.startsWith('### ')) {
      blocks.push(html`<h3>${renderInlineMarkdown(trimmed.slice(4))}</h3>`);
    } else if (trimmed.startsWith('## ')) {
      blocks.push(html`<h2>${renderInlineMarkdown(trimmed.slice(3))}</h2>`);
    } else if (trimmed.startsWith('# ')) {
      blocks.push(html`<h1>${renderInlineMarkdown(trimmed.slice(2))}</h1>`);
    } else {
      blocks.push(html`<p>${renderInlineMarkdown(trimmed)}</p>`);
    }
  }

  flushCode();
  flushList();
  if (markdownArg.split('\n').length > maxLinesArg) {
    blocks.push(html`<p class="muted">Markdown preview limited to first ${maxLinesArg} lines.</p>`);
  }
  return blocks;
}
